import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { formatMoneyFromUnknown, resolveCurrencyCode } from '@/lib/money'
import { useCurrentStore } from '../../context/CurrentStoreContext'
import { useTransaction } from '../../context/TransactionContext'
import { Card, CardHeader } from '../../components/ui/Card'
import { SkeletonListPage } from '../../components/ui/Skeleton'
import { Button } from '../../components/ui/Button'
import { Select } from '../../components/ui/Select'
import type { ActivityChannel } from '../../services/transactionService'

const DEFAULT_LIMIT = 20

export default function TransactionList() {
  const { transactions, listTotal, listLoading, error, fetchTransactions } = useTransaction()
  const { storeRecord } = useCurrentStore()
  const storeCurrency = resolveCurrencyCode(storeRecord?.currency)
  const [page, setPage] = useState(1)
  const [limit, setLimit] = useState(DEFAULT_LIMIT)
  const [channel, setChannel] = useState<ActivityChannel>('all')

  const load = useCallback(() => {
    return fetchTransactions({ page, limit, channel })
  }, [fetchTransactions, page, limit, channel])

  useEffect(() => {
    load().catch(() => {})
  }, [load])

  const totalPages = Math.max(1, Math.ceil(listTotal / limit) || 1)
  const showingFrom =
    listTotal === 0 ? 0 : (page - 1) * limit + (transactions.length === 0 ? 0 : 1)
  const showingTo = listTotal === 0 ? 0 : (page - 1) * limit + transactions.length

  return (
    <Card>
      <CardHeader
        title="Transactions"
        description="Wallet, payouts, online store checkouts, and in-store POS — filter by channel to compare how sales come in."
      />
      <div className="mb-4 flex flex-wrap items-end gap-3">
        <label className="flex flex-col gap-1.5">
          <span className="text-xs font-medium text-muted-foreground">Channel</span>
          <Select
            className="min-w-[200px]"
            value={channel}
            disabled={listLoading}
            onChange={(e) => {
              setChannel(e.target.value as ActivityChannel)
              setPage(1)
            }}
          >
            <option value="all">All activity</option>
            <option value="storefront">Online store</option>
            <option value="in_store">In-store (POS)</option>
          </Select>
        </label>
      </div>
      {error ? <p className="mb-4 text-sm text-destructive">{error}</p> : null}
      {listLoading ? (
        <SkeletonListPage columns={7} rows={6} />
      ) : (
        <>
          <div className="overflow-x-auto rounded-xl border border-border">
            <table className="min-w-full divide-y divide-border text-sm">
              <thead className="bg-muted/30 text-left text-muted-foreground">
                <tr>
                  <th className="px-4 py-3 font-medium">S/N</th>
                  <th className="px-4 py-3 font-medium">Reference</th>
                  <th className="px-4 py-3 font-medium">Channel</th>
                  <th className="px-4 py-3 font-medium">Order / note</th>
                  <th className="px-4 py-3 font-medium">Amount</th>
                  <th className="px-4 py-3 font-medium">Status</th>
                  <th className="px-4 py-3 font-medium">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border bg-card">
                {transactions.map((item, rowIndex) => (
                  <tr key={item.listKey || item.id}>
                    <td className="px-4 py-3 tabular-nums">{(page - 1) * limit + rowIndex + 1}</td>
                    <td className="px-4 py-3">{item.reference || item.id}</td>
                    <td className="px-4 py-3 text-muted-foreground">{item.channelLabel || '—'}</td>
                    <td className="px-4 py-3">{item.orderNumber || '-'}</td>
                    <td className="px-4 py-3 tabular-nums">
                      {item.amount != null && item.amount !== ''
                        ? formatMoneyFromUnknown(
                            item.amount,
                            resolveCurrencyCode(item.currency || storeCurrency),
                          )
                        : '-'}
                    </td>
                    <td className="px-4 py-3">{item.status || '-'}</td>
                    <td className="px-4 py-3">
                      <Link
                        to={`/transactions/${encodeURIComponent(item.rawRef ?? item.id)}`}
                        className="text-primary hover:underline"
                      >
                        View
                      </Link>
                    </td>
                  </tr>
                ))}
                {transactions.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-4 py-6 text-center text-muted-foreground">
                      No transactions available.
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>

          {listTotal > 0 ? (
            <div className="mt-4 flex flex-col gap-3 border-t border-border pt-4 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-xs text-muted-foreground">
                Showing{' '}
                <span className="font-medium text-foreground">{showingFrom}</span>
                –
                <span className="font-medium text-foreground">{showingTo}</span> of{' '}
                <span className="font-medium text-foreground">{listTotal}</span>
              </p>
              <div className="flex flex-wrap items-center gap-2">
                <label className="flex items-center gap-2 text-xs text-muted-foreground">
                  Per page
                  <select
                    className="rounded-lg border border-border bg-card px-2 py-1.5 text-sm font-medium text-foreground"
                    value={limit}
                    disabled={listLoading}
                    onChange={(e) => {
                      setLimit(Number(e.target.value))
                      setPage(1)
                    }}
                  >
                    {[10, 20, 50, 100].map((n) => (
                      <option key={n} value={n}>
                        {n}
                      </option>
                    ))}
                  </select>
                </label>
                <Button
                  type="button"
                  variant="secondary"
                  className="!px-3 !py-1.5 text-xs"
                  disabled={page <= 1 || listLoading}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                >
                  Previous
                </Button>
                <span className="text-xs font-medium text-foreground">
                  Page {page} of {totalPages}
                </span>
                <Button
                  type="button"
                  variant="secondary"
                  className="!px-3 !py-1.5 text-xs"
                  disabled={page >= totalPages || listLoading}
                  onClick={() => setPage((p) => p + 1)}
                >
                  Next
                </Button>
              </div>
            </div>
          ) : null}
        </>
      )}
    </Card>
  )
}
